import { useRouteError } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { reportError } from '@/lib/errorReporter';

const isChunkLoadError = (error: unknown): boolean => {
  const msg = error instanceof Error ? error.message : String(error);
  return (
    msg.includes('<!DOCTYPE') ||
    msg.includes('is not valid JSON') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Unable to preload CSS') ||
    msg.includes('Unexpected token') ||
    msg.includes('ChunkLoadError') ||
    msg.includes('Loading chunk') ||
    msg.includes('Minified React error #423') ||
    msg.includes('Minified React error #418') ||
    msg.includes('Minified React error #425') ||
    msg.includes('hydrat')
  );
};

export default function RootErrorBoundary() {
  const error = useRouteError();
  const { t } = useTranslation('common');

  let reloadCount = 0;
  try {
    reloadCount = isChunkLoadError(error)
      ? parseInt(sessionStorage.getItem('chunk-reload-count') || '0')
      : 0;
  } catch { /* sessionStorage unavailable (private browsing, etc.) */ }
  const shouldAutoReload = isChunkLoadError(error) && reloadCount < 2;

  useEffect(() => {
    // Skip reporting when we're about to auto-reload — the user recovers
    // silently and the digest stays focused on errors that actually broke
    // something. Only report when the fallback UI is what the user sees:
    // a non-chunk error, or a chunk error past the retry budget.
    if (error && !shouldAutoReload) {
      reportError(error, {
        component: 'RootErrorBoundary',
        isChunkLoad: isChunkLoadError(error),
      });
    }
  }, [error, shouldAutoReload]);

  useEffect(() => {
    if (shouldAutoReload) {
      // New deployment: old chunks are gone — reload to get the latest version
      try { sessionStorage.setItem('chunk-reload-count', String(reloadCount + 1)); } catch { /* ignore */ }
      window.location.reload();
    } else if (isChunkLoadError(error)) {
      // Max retries reached — clear counter so future navigations can retry
      try { sessionStorage.removeItem('chunk-reload-count'); } catch { /* ignore */ }
    }
  }, [error, shouldAutoReload, reloadCount]);

  if (shouldAutoReload) {
    // Show nothing while reloading
    return null;
  }

  return (
    <div className="min-h-screen bg-white dark:bg-graphite-900 flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">{t('error_something_went_wrong')}</h1>
        <p className="text-slate-500 dark:text-slate-400 mb-6 text-sm">
          {t('error_unexpected')}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-[var(--color-brand-blue)] hover:bg-[var(--color-brand-blue-hover)] text-white rounded-lg text-sm font-medium transition-colors"
        >
          {t('error_refresh_page')}
        </button>
      </div>
    </div>
  );
}
